import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";

/**
 * SEO Component for managing meta tags across the application.
 * Handles page title, meta description, Open Graph, and Twitter Cards.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title (required)
 * @param {string} props.description - Meta description
 * @param {string} props.canonical - Canonical URL for the page
 * @param {string} props.ogType - Open Graph type (default: "website")
 * @param {string} props.ogImage - Open Graph image URL
 * @param {number} props.ogImageWidth - OG image width (default: 1200)
 * @param {number} props.ogImageHeight - OG image height (default: 630)
 * @param {string} props.twitterCard - Twitter card type (default: "summary_large_image")
 * @param {boolean} props.noIndex - If true, adds noindex meta tag
 */
const SEO = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  ogImageWidth = 1200,
  ogImageHeight = 630,
  twitterCard = "summary_large_image",
  noIndex = false,
}) => {
  // Generate full title with site name suffix
  const fullTitle = title ? `${title} | Movebooth` : "Movebooth";

  // Default description if none provided
  const metaDescription =
    description ||
    "Create and share memorable photo booth moments with Movebooth - the modern photo booth platform for events.";

  // Use canonical URL or current page URL
  const pageUrl = canonical || (typeof window !== "undefined" ? window.location.href : "");

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {canonical && <link rel="canonical" href={canonical} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content="Movebooth" />
      {ogImage && (
        <>
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content={String(ogImageWidth)} />
          <meta property="og:image:height" content={String(ogImageHeight)} />
          <meta property="og:image:alt" content={fullTitle} />
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string,
  ogType: PropTypes.string,
  ogImage: PropTypes.string,
  ogImageWidth: PropTypes.number,
  ogImageHeight: PropTypes.number,
  twitterCard: PropTypes.oneOf([
    "summary",
    "summary_large_image",
    "app",
    "player",
  ]),
  noIndex: PropTypes.bool,
};

export default SEO;
